// src/features/sheets/components/SpreadsheetStatusBar.tsx
// Status bar component for spreadsheet

import React from "react";
import type { CellPosition, FillDragState } from "../types";
import { colIndexToLetter } from "../utils/formulaEngine";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface SpreadsheetStatusBarProps {
  rows: number;
  cols: number;
  selectedCell: CellPosition | null;
  editingCell: CellPosition | null;
  fillDrag: FillDragState | null;
  fillPreviewCellCount: number;
  isFormula?: boolean | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const SpreadsheetStatusBar: React.FC<SpreadsheetStatusBarProps> = ({
  rows,
  cols,
  selectedCell,
  editingCell,
  fillDrag,
  fillPreviewCellCount,
  isFormula,
}) => {
  return (
    <div className="flex items-center gap-3 px-2 py-1 border-t border-slate-700/50 bg-slate-800/30 text-[10px] text-slate-500 flex-shrink-0">
      {/* Grid size */}
      <span>
        {rows} × {cols}
      </span>

      {/* Selected cell info */}
      {selectedCell && (
        <>
          <span>
            {colIndexToLetter(selectedCell.col)}
            {selectedCell.row + 1}
          </span>
          {isFormula && <span className="text-blue-400">Formula</span>}
        </>
      )}

      {/* Status message */}
      <span className="ml-auto">
        {fillDrag && fillPreviewCellCount > 0
          ? `Filling ${fillPreviewCellCount} cell${fillPreviewCellCount > 1 ? "s" : ""}`
          : editingCell
          ? "Editing"
          : "Ready"}{" "}
        • {fillDrag ? "Release to fill" : "Arrow keys to navigate"}
      </span>
    </div>
  );
};

export default SpreadsheetStatusBar;
